import { useCallback, useMemo } from 'react';

import { BridgeSubscriptionMethod, EventSubscriptionConfig } from '@scaleaq/sitecontrol-lib';
import { useQuery } from '@tanstack/react-query';

import { useSiteContext } from '@/providers';

import { useCurrentSiteConnection } from '../connection';
import { updateSubscribedToEvent, useBridgeEventSubscription } from '../registerAndSubscribe';

type useEventSubscriptionProps = {
  eventNames: string[];
  listenerId: string;
  includeEphemeral?: boolean;
};

export const useEventSubscription = ({
  eventNames,
  listenerId,
  includeEphemeral = true,
}: useEventSubscriptionProps) => {
  const { currentSite } = useSiteContext();
  const { connectionId } = useCurrentSiteConnection();

  const queryKey = useMemo<string[]>(() => {
    return [`multiple-sensors-${currentSite?.id}-${eventNames.join('-')}`, connectionId];
  }, [connectionId, currentSite?.id, eventNames]);

  const { subscribeToEvent, isLoading, isError } =
    useBridgeEventSubscription<EventSubscriptionConfig>(BridgeSubscriptionMethod.SubscribeByEvent);

  const getter = useCallback(async () => {
    if (isLoading) {
      return false;
    }
    try {
      await Promise.all(
        eventNames.map(async eventName => {
          const subscriptionArgs: EventSubscriptionConfig = {
            eventName: eventName,
            listenerId: listenerId,
            includeEphemeral,
          };
          await subscribeToEvent(subscriptionArgs);
        })
      );
      updateSubscribedToEvent({
        eventNamesOrIds: eventNames,
        currentSiteId: currentSite?.id ?? '',
      });

      return true;
    } catch (e) {
      console.error('Error subscribing to events: ', eventNames, e);
      return false;
    }
  }, [currentSite?.id, eventNames, includeEphemeral, isLoading, listenerId, subscribeToEvent]);

  const query = useQuery<boolean>({
    enabled: !isLoading,
    queryKey: queryKey,
    queryFn: getter,
  });
  return { ...query, isLoading: query.isLoading || isLoading, isError: query.isError || isError };
};
